import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { ImageViewer } from '@/components/ui/ImageViewer';
import { ReorderControls, CardLoadingOverlay } from '@/components/ui/ReorderControls';
import { 
  Trash2, 
  Image as ImageIcon
} from 'lucide-react';
import type { ImageMenuItem } from '@/types';

interface ImageMenuGalleryProps {
  images: ImageMenuItem[];
  onDeleteImage: (image: ImageMenuItem) => void;
  onReorderImages: (reorderedImages: ImageMenuItem[]) => void;
  loading: boolean;
  deletingImageId?: string; // Track which image is being deleted
  reorderingImageIds: string[]; // Track which images are being reordered (both affected)
}

/**
 * ImageMenuGallery - Display and manage uploaded images
 * Features:
 * - Grid layout with hover effects
 * - Image expansion with zoom
 * - Delete confirmation
 * - Up/down arrow reordering (replacing drag and drop)
 * - Touch-friendly mobile interface
 */
const ImageMenuGallery: React.FC<ImageMenuGalleryProps> = ({
  images,
  onDeleteImage,
  onReorderImages,
  loading,
  deletingImageId,
  reorderingImageIds
}) => {
  const [expandedImageIndex, setExpandedImageIndex] = useState<number | null>(null);
  // State to track which images are currently loading and which have finished loading
  const [loadingImages, setLoadingImages] = useState<Set<string>>(new Set());
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());

  // Initialize loading state for new images only
  useEffect(() => {
    const currentImageIds = new Set(images.map(img => img.id));
    
    setLoadingImages(prev => {
      const newLoadingSet = new Set<string>();
      
      // For each current image, decide if it should be loading
      images.forEach(img => {
        // If image is not loaded and not currently loading, start loading
        if (!loadedImages.has(img.id) && !prev.has(img.id)) {
          newLoadingSet.add(img.id);
        } 
        // If image is currently loading and still exists, keep it loading
        else if (prev.has(img.id) && currentImageIds.has(img.id)) {
          newLoadingSet.add(img.id);
        }
        // Note: If image is already loaded (in loadedImages), it won't be added to loading state
      });
      
      return newLoadingSet;
    });

    // Clean up loaded images state for deleted images to prevent memory leaks
    setLoadedImages(prev => {
      const newLoadedSet = new Set<string>();
      prev.forEach(imageId => {
        if (currentImageIds.has(imageId)) {
          newLoadedSet.add(imageId);
        }
      });
      return newLoadedSet;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images]); // Only depend on images array, not loadedImages to avoid circular updates

  const handleImageLoad = (imageId: string) => {
    setLoadingImages(prev => {
      const newSet = new Set(prev);
      newSet.delete(imageId);
      return newSet;
    });
    setLoadedImages(prev => {
      const newSet = new Set(prev);
      newSet.add(imageId);
      return newSet;
    });
  };

  const handleImageError = (imageId: string) => {
    setLoadingImages(prev => {
      const newSet = new Set(prev);
      newSet.delete(imageId);
      return newSet;
    });
    // Don't add to loadedImages on error, so it can retry loading if needed
  };

  const expandImage = (index: number) => {
    setExpandedImageIndex(index);
  };

  const closeExpandedImage = () => {
    setExpandedImageIndex(null);
  };

  const navigateImage = (direction: 'prev' | 'next') => {
    if (expandedImageIndex === null) return;
    
    if (direction === 'prev') {
      setExpandedImageIndex(
        expandedImageIndex > 0 ? expandedImageIndex - 1 : images.length - 1
      );
    } else {
      setExpandedImageIndex(
        expandedImageIndex < images.length - 1 ? expandedImageIndex + 1 : 0
      );
    }
  };

  // Handle reordering with proper order property update
  const handleReorderImages = (reorderedImages: ImageMenuItem[]) => {
    // Update order property for each image
    const imagesWithUpdatedOrder = reorderedImages.map((img, index) => ({
      ...img,
      order: index
    }));
    
    onReorderImages(imagesWithUpdatedOrder);
  };

  // Show loading state
  if (loading && images.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
          <ImageIcon className="w-6 h-6 text-white" />
        </div>
        <p className="text-gray-600">Loading images...</p>
      </div>
    );
  }

  return (
    <>
      {/* Image Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {images.map((image, index) => (
          <div
            key={image.id}
            data-image-id={image.id}
            className="group relative bg-white rounded-xl overflow-hidden border-2 transition-all duration-200 select-none border-gray-200"
            style={{
              opacity: index < images.length ? 1 : 0,
              transform: `scale(${index < images.length ? 1 : 0.9})`,
              transition: `opacity 200ms ease ${index * 50}ms, transform 200ms ease ${index * 50}ms`,
              userSelect: 'none',
              WebkitUserSelect: 'none',
              MozUserSelect: 'none',
              msUserSelect: 'none'
            }}
          >
            {/* Loading Overlay with proper corner handling */}
            <CardLoadingOverlay 
              loading={reorderingImageIds.includes(image.id)} 
              rounded="rounded-xl"
            />
            
            {/* Image - Clickable for expansion */}
            <div className="aspect-square cursor-pointer select-none relative" onClick={() => expandImage(index)}>
              {/* Loading Skeleton */}
              {loadingImages.has(image.id) && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                  <motion.div
                    animate={{ 
                      opacity: [0.5, 1, 0.5]
                    }}
                    transition={{ 
                      duration: 1.5, 
                      repeat: Infinity, 
                      ease: 'easeInOut' 
                    }}
                    className="w-full h-full"
                  />
                </div>
              )}
              
              <img
                src={image.thumbnailUrl || image.imageUrl}
                alt={image.fileName}
                className={`w-full h-full object-cover select-none pointer-events-none transition-opacity ${
                  deletingImageId === image.id ? 'opacity-50' : 'opacity-100'
                } ${loadingImages.has(image.id) ? 'opacity-0' : 'opacity-100'}`}
                loading="lazy"
                draggable={false}
                onLoad={() => handleImageLoad(image.id)}
                onError={() => handleImageError(image.id)}
              />
              
              {/* Deletion Loading Overlay */}
              {deletingImageId === image.id && (
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                  <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}
            </div>

            {/* Footer with Controls - Always visible */}
            <div className="p-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between select-none">
              {/* Reorder Controls */}
              <ReorderControls
                item={image}
                items={images}
                onReorder={handleReorderImages}
                getItemId={(image) => image.id}
                isAnyReordering={reorderingImageIds.length > 0}
              />
              
              {/* Delete Button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onDeleteImage(image);
                }}
                disabled={deletingImageId === image.id}
                className="p-1 text-red-600 hover:text-red-700 hover:bg-red-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                title="Delete image"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Image Viewer */}
      <ImageViewer
        images={images.map(img => ({
          id: img.id,
          imageUrl: img.imageUrl,
          alt: img.fileName
        }))}
        currentIndex={expandedImageIndex}
        onClose={closeExpandedImage}
        onNavigate={navigateImage}
        showInfo={true}
      />
    </>
  );
};

export default ImageMenuGallery;